import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';

export interface Warehouse {
  id: string;
  name: string;
  description: string;
  address: string;
  additionalAddressInfo?: string;
  contact: string;
  pictureIds: string[];
  location: {
    latitude: number;
    longitude: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface WarehouseCreateEntry {
  name: string;
  description: string;
  address: string;
  additionalAddressInfo?: string;
  contact: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

export interface WarehouseListResponse {
  data: Warehouse[];
  total?: number;
}

class WarehousesService {
  private apiRoot = configs.apiRoot;

  async getWarehouses(params?: {
    page?: number;
    perPage?: number;
    filter?: any;
    sort?: any;
  }): Promise<Warehouse[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.perPage) queryParams.append('size', params.perPage.toString());
    if (params?.filter) queryParams.append('filter', JSON.stringify(params.filter));
    if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

    const url = `${this.apiRoot}/warehouses${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch warehouses: ${response.statusText}`
      );
    }

    const result = await response.json();
    return Array.isArray(result) ? result : result.data || [];
  }

  async getWarehouse(id: string): Promise<Warehouse> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/warehouses/${id}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch warehouse: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async createWarehouse(data: WarehouseCreateEntry): Promise<Warehouse> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/warehouses`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to create warehouse: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async updateWarehouse(
    id: string,
    data: Partial<WarehouseCreateEntry>
  ): Promise<Warehouse> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/warehouses/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to update warehouse: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async deleteWarehouse(id: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/warehouses/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to delete warehouse: ${response.statusText}`
      );
    }
  }
}

export const warehousesService = new WarehousesService();

